import React from "react";
import {
  Button,
  Divider,
  Form,
  FormInstance,
  Input,
  Modal,
  Select,
  Tag,
  UploadFile,
  Checkbox,
  Space,
  Tooltip,
  message,
} from "antd";
import { IUser } from "../../types/interfaces";
import CountrySelect from "../general/countrySelect/countrySelect";
import PhonePrefixSelect from "../general/phonePrefixSelect/phonePrefixSelect";
import { IDENTITY_TYPE_OPTIONS, USER_TYPES } from "../../constants/constants";
import UploadButton from "../general/uploadButton/uploadButton";
import useAuth from "../../hooks/useAuth";
import isAllowed from "../../utils/isAllowed";
import { CalendarDays, Crown, Users, Copy } from "lucide-react";
import { getPermissionLabel } from "../../constants/permissions";
import { PermissionBadgeList } from "../general/permissionBadge/PermissionBadge";

interface Props {
  isModalOpen: boolean;
  form: FormInstance<IUser>;
  handleCancel: () => void;
  handleFileChange: (file: UploadFile | null, name: string) => void;
  handleOk: () => void;
  uploads: {
    [key: string]: {
      file: any;
    };
  };
  editing?: boolean;
  loading?: boolean;
  url?: string;
  current?: any;
  dynamicRoleOptions?: any[];
}

function UserModal({
  editing,
  form,
  isModalOpen,
  handleCancel,
  handleFileChange,
  handleOk,
  loading,
  uploads,
  url,
  current,
  dynamicRoleOptions = [],
}: Props) {
  const { rolePermissions, userData: user } = useAuth();
  
  const userType = Form.useWatch("type", form);
  const selectedCompanyName = Form.useWatch("companyName", form);
  const [plans, setPlans] = React.useState<any[]>([]);
  const [companies, setCompanies] = React.useState<any[]>([]);

  // Extract Target Subscription from the User being inspected (current)
  let targetSubscription = null;
  let targetSubscriptionPlan = null;

  if (current && Object.keys(current).length > 0) {
    if (current?.type === "empresa") {
      if (current?.subscription && typeof current.subscription === 'object') {
        targetSubscription = current.subscription;
      } else if (current?.edges?.subscription) {
        targetSubscription = Array.isArray(current.edges.subscription) ? current.edges.subscription[0] : current.edges.subscription;
      }
    } else {
      // Para empleados
      if (current?.subscription && typeof current.subscription === 'object') {
        targetSubscription = current.subscription;
      } else {
        const belongsTo = current?.edges?.belongsToCompany?.[0];
        if (belongsTo?.edges?.subscription) {
          targetSubscription = Array.isArray(belongsTo.edges.subscription) ? belongsTo.edges.subscription[0] : belongsTo.edges.subscription;
        }
      }
    }
  }

  // Fallback si no pudimos extraerlo de current (por ejemplo, al crear un empleado)
  if (!targetSubscription) {
    if (user?.type === "empresa") {
      targetSubscription = user?.subscription;
    } else if (selectedCompanyName && (user?.type === "admin" || user?.type === "super_admin" || user?.type === "gestor")) {
      const selectedCompanyUser = companies.find(c => {
        const compName = c?.edges?.belongsToCompany?.[0]?.name || c.name;
        return compName === selectedCompanyName;
      });
      if (selectedCompanyUser) {
        targetSubscription = selectedCompanyUser.subscription || 
          (Array.isArray(selectedCompanyUser.edges?.subscription) 
            ? selectedCompanyUser.edges.subscription[0] 
            : selectedCompanyUser.edges?.subscription);
      }
    }
  }

  // Extract targetSubscriptionPlan
  if (current?.plan && typeof current.plan === 'object') {
    targetSubscriptionPlan = current.plan;
  } else if (targetSubscription?.edges?.subscriptionPlan) {
    targetSubscriptionPlan = Array.isArray(targetSubscription.edges.subscriptionPlan) 
      ? targetSubscription.edges.subscriptionPlan[0] 
      : targetSubscription.edges.subscriptionPlan;
  } else if (targetSubscription?.plan) {
    targetSubscriptionPlan = targetSubscription.plan;
  }

  // Fallback para plan
  if (!targetSubscriptionPlan && user?.type === "empresa") {
    targetSubscriptionPlan = user?.plan;
  } else if (!targetSubscriptionPlan && selectedCompanyName && (user?.type === "admin" || user?.type === "super_admin" || user?.type === "gestor")) {
    const selectedCompanyUser = companies.find(c => {
      const compName = c?.edges?.belongsToCompany?.[0]?.name || c.name;
      return compName === selectedCompanyName;
    });
    if (selectedCompanyUser) {
      targetSubscriptionPlan = selectedCompanyUser.plan || 
        (selectedCompanyUser.edges?.subscription && 
          (Array.isArray(selectedCompanyUser.edges.subscription) 
            ? selectedCompanyUser.edges.subscription[0]?.edges?.subscriptionPlan 
            : selectedCompanyUser.edges.subscription?.edges?.subscriptionPlan));
    }
  }
  
  let availablePlanPermissions: string[] = [];
  if (targetSubscriptionPlan?.permissions && Array.isArray(targetSubscriptionPlan.permissions)) {
     targetSubscriptionPlan.permissions.forEach((p: any) => {
        if (typeof p === "string") availablePlanPermissions.push(p);
        else if (p.name) availablePlanPermissions.push(p.name);
     });
  } else if (targetSubscriptionPlan?.edges?.permissions) {
     targetSubscriptionPlan.edges.permissions.forEach((p: any) => {
        if (p.name) availablePlanPermissions.push(p.name);
     });
  }
  

  React.useEffect(() => {
    if (isModalOpen && (user?.type === "admin" || user?.type === "super_admin" || user?.type === "gestor")) {
      import("../../services/subscriptionPlan").then(({ getSubscriptionPlans }) => {
        getSubscriptionPlans().then((res: any) => setPlans(res?.data || []));
      });
      import("../../services/users").then(({ getUsers }) => {
        getUsers({ type: "empresa", limit: 1000, page: 1 }).then((res: any) => setCompanies(res?.data || []));
      });
    }
  }, [isModalOpen, user?.type]);
  const userTypesList = dynamicRoleOptions.filter((userType) => {
    // Si no es un rol de sistema quemado, lo permitimos si tiene permisos para asignar empleados o si es empresa
    const isSystemRole = ["admin", "gestor", "empresa", "empleado"].includes(userType.value);
    
    if (!isSystemRole) {
      return true; // Permitimos roles dinámicos
    }

    return isAllowed({
      permissions: rolePermissions,
      permission: `ui:users:update:type:${userType.value}`,
    });
  });

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  return (
    <Modal
      title={editing ? "Editar usuario" : "Agregar usuario"}
      open={isModalOpen}
      onOk={handleOk}
      width={650}
      closable={false}
      footer={[
        <Button
          key="submit"
          id={"submit"}
          type="primary"
          onClick={handleOk}
          loading={loading}
          style={{
            background: "#81BC3D",
            borderColor: "#81BC3D",
            borderRadius: "8px",
            fontWeight: 600,
          }}
        >
          {editing ? "Actualizar" : "Guardar"}
        </Button>,
        <Button
          key="back"
          onClick={handleCancel}
          style={{ borderRadius: "8px" }}
        >
          Cancelar
        </Button>,
      ]}
      closeIcon={false}
      destroyOnClose={true}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        {/* ─── SECCIÓN SUPERIOR: Foto + Suscripción ─── */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "20px",
            marginBottom: "8px",
          }}
        >
          <UploadButton
            name={"photo"}
            onFileChange={handleFileChange}
            file={uploads["photo"]?.file}
            url={url}
            usePreview
          />

          {/* Tarjeta de suscripción */}
          {editing && targetSubscriptionPlan && (
            <div
              style={{
                flex: 1,
                background:
                  "linear-gradient(135deg, #f0fbe4 0%, #e8f5d6 50%, #dff0cc 100%)",
                borderRadius: "12px",
                padding: "14px 18px",
                border: "1px solid #c5e6a0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Crown size={16} color="#81BC3D" />
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "15px",
                      color: "#3a6b0a",
                    }}
                  >
                    {targetSubscriptionPlan.name}
                  </span>
                </div>
                <Tag
                  color={targetSubscription?.active ? "green" : "red"}
                  style={{ borderRadius: "6px", fontWeight: 600 }}
                >
                  {targetSubscription?.active ? "Vigente" : "Inactiva"}
                </Tag>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  fontSize: "12px",
                  color: "#5a8a1a",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CalendarDays size={13} />
                  <span>
                    <strong>Desde:</strong>{" "}
                    {formatDate(targetSubscription?.startDate)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CalendarDays size={13} />
                  <span>
                    <strong>Hasta:</strong> {formatDate(targetSubscription?.endDate)}
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "6px",
                  fontSize: "12px",
                  color: "#5a8a1a",
                }}
              >
                <Users size={13} />
                <span>
                  Límite: <strong>{targetSubscriptionPlan.userLimit}</strong> usuarios
                </span>
              </div>
            </div>
          )}
        </div>

        <Divider
          style={{ margin: "8px 0 16px 0", borderColor: "#e8e8e8" }}
        />

        {/* ─── Fila 1: Nombre + Tipo ─── */}
        <div className={"modal-row"}>
          <Form.Item
            name="name"
            label="Nombre completo"
            rules={[
              { required: true, message: "Nombre de usuario es requerido" },
            ]}
          >
            <Input
              placeholder="Nombre del usuario"
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Tipo de usuario"
            rules={[
              { required: true, message: "Tipo de usuario es requerido" },
            ]}
          >
            <Select
              allowClear
              style={{ width: "100%" }}
              options={userTypesList}
              placeholder="Seleccionar tipo"
              disabled={
                !isAllowed({
                  permissions: rolePermissions,
                  permission: "ui:users:update:type",
                })
              }
            />
          </Form.Item>
        </div>

        {userType === "empresa" && (
          <div className={"modal-row"}>
            <Form.Item
              name="companyName"
              label="Nombre de la empresa"
              rules={[{ required: true, message: "Nombre de empresa requerido" }]}
            >
              <Input placeholder="Tech Corp S.A." style={{ borderRadius: "8px" }} disabled={editing && user?.type !== "admin"} />
            </Form.Item>
            <Form.Item
              name="companyWebsite"
              label="Sitio web de la empresa (Opcional)"
            >
              <Input placeholder="https://..." style={{ borderRadius: "8px" }} disabled={editing && user?.type !== "admin"} />
            </Form.Item>
          </div>
        )}



        {userType !== "admin" && userType !== "gestor" && userType !== "empresa" && userType != null && (
          <div className={"modal-row"}>
            <Form.Item
              name="companyName"
              label="Pertenece a la empresa"
              rules={[{ required: true, message: "Empresa es requerida" }]}
            >
              {(user?.type === "admin" || user?.type === "super_admin" || user?.type === "gestor") ? (
                <Select
                  placeholder="Seleccionar empresa"
                  options={companies.map((c) => {
                    const compName = c?.edges?.belongsToCompany?.[0]?.name;
                    const val = compName || c.name;
                    const lbl = compName ? `${compName} (${c.name})` : c.name;
                    return { label: lbl, value: val };
                  })}
                  style={{ width: "100%", borderRadius: "8px" }}
                />
              ) : (
                <Input disabled style={{ borderRadius: "8px" }} />
              )}
            </Form.Item>

            <Form.Item
              name="position"
              label="Cargo"
              rules={[{ required: false }]}
            >
              <Input
                placeholder="Ej. Gerente de Operaciones, Analista..."
                style={{ borderRadius: "8px" }}
              />
            </Form.Item>
          </div>
        )}

        {/* ─── Fila 2: Email + Password (sólo creación) ─── */}
        <div className={!editing ? "modal-row" : ""}>
          <Form.Item
            name="email"
            label="Correo electrónico"
            rules={[
              { required: true, message: "Correo electrónico es requerido" },
            ]}
          >
            <Input
              placeholder="correo@ejemplo.com"
              style={{ borderRadius: "8px" }}
            />
          </Form.Item>

          {!editing && (
            <Form.Item
              name="password"
              label="Contraseña provisional"
              rules={[
                { required: true, message: "La contraseña provisional es obligatoria" },
                { min: 8, message: "Mínimo 8 caracteres" },
                { pattern: /[A-Z]/, message: "Debe incluir una letra mayúscula" },
                { pattern: /[a-z]/, message: "Debe incluir una letra minúscula" },
                { pattern: /[0-9]/, message: "Debe incluir un número" },
                { pattern: /[^A-Za-z0-9]/, message: "Debe incluir un carácter especial" }
              ]}
              tooltip="Contraseña temporal generada automáticamente. Puede modificarla si lo desea, pero debe cumplir con las políticas de seguridad (mínimo 8 caracteres, 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial)."
            >
              <Input
                placeholder="Contraseña provisional"
                style={{ borderRadius: "8px" }}
                suffix={
                  <Tooltip title="Copiar">
                    <Copy
                      size={16}
                      style={{ cursor: "pointer", color: "#8c8c8c" }}
                      onClick={() => {
                        const pwd = form.getFieldValue("password" as any);
                        if (pwd) {
                          navigator.clipboard.writeText(pwd);
                          message.success("Contraseña copiada");
                        }
                      }}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>
          )}
        </div>

        {/* ─── Fila 3: País + Teléfono ─── */}
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 2fr", gap: "16px" }}>
          <CountrySelect 
            onChange={(val) => {
              if (val) {
                form.setFieldsValue({ phonePrefix: val });
              }
            }}
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "140px 1fr",
              gap: "8px",
            }}
          >
            <PhonePrefixSelect />
            <Form.Item
              name="phone"
              label="Teléfono"
              rules={[{ required: false }]}
            >
              <Input
                placeholder="Teléfono"
                style={{ width: "100%", borderRadius: "8px" }}
              />
            </Form.Item>
          </div>
        </div>

        {/* ─── Fila 4: Tipo de identidad + Número ─── */}
        <div className={"modal-row"}>
          <Form.Item
            name="identityType"
            label="Tipo de identificación"
            rules={[{ required: false }]}
          >
            <Select
              placeholder="Seleccionar tipo"
              style={{ width: "100%" }}
              options={IDENTITY_TYPE_OPTIONS}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="identityNumber"
            label="Número de identificación"
            rules={[{ required: false }]}
          >
            <Input
              placeholder="0000000"
              style={{ width: "100%", borderRadius: "8px" }}
            />
          </Form.Item>
        </div>

        {/* ─── Fila 5: Notas (opcional) ─── */}
        <Form.Item name="notes" label="Anotaciones / Referencias">
          <Input.TextArea
            rows={2}
            placeholder="Notas internas sobre el usuario..."
            style={{ borderRadius: "8px" }}
          />
        </Form.Item>

        {/* ─── Fila 6: Vistas Permitidas (Filtro por Empresa) ─── */}
        {!(userType === "empresa" && targetSubscription?.active) && (
          <>
            <Divider style={{ margin: "16px 0", borderColor: "#e8e8e8" }} orientation="left">Accesos y Vistas Permitidas</Divider>
            
            {(userType !== "admin" && userType !== "gestor") && (!targetSubscription || !targetSubscription?.active) ? (
              <div style={{ 
                marginTop: "0px", 
                marginBottom: "16px",
                padding: "12px 16px", 
                background: "#fffbe6", 
                border: "1px solid #ffe58f", 
                borderRadius: "8px",
                color: "#d48806",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <span style={{ fontWeight: 600 }}>Nota:</span> El usuario no cuenta con una suscripción activa.
              </div>
            ) : (
              <Form.Item
                label={(userType !== "admin" && userType !== "gestor" && userType !== "empresa") && isAllowed({ permissions: rolePermissions, permission: "ui:users:update:permissions" }) ? "Mapas y Boletines (Activa o desactiva accesos de tu plan)" : "Mapas y Boletines (Límites de tu plan activo)"}
              >
                {(userType !== "admin" && userType !== "gestor" && userType !== "empresa") && isAllowed({ permissions: rolePermissions, permission: "ui:users:update:permissions" }) ? (
                  <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                    <Form.Item name="permissions" noStyle>
                      <Checkbox.Group style={{ width: "100%" }}>
                      <div style={{ width: "100%" }}>
                        {availablePlanPermissions.some((p) => p.includes("ui:map")) && (
                          <div style={{ marginBottom: 16 }}>
                            <h4 style={{ color: "#1890ff", marginBottom: 8, marginTop: 0 }}>Mapas</h4>
                            <Space direction={"horizontal"} wrap>
                              {availablePlanPermissions
                                .filter((p) => p.includes("ui:map"))
                                .map((p) => (
                                  <Checkbox value={p} key={p}>
                                    {getPermissionLabel(p)}
                                  </Checkbox>
                                ))}
                            </Space>
                          </div>
                        )}
                        {availablePlanPermissions.some((p) => p.includes("ui:newsletter")) && (
                          <div style={{ marginBottom: 8 }}>
                            <h4 style={{ color: "#52c41a", marginBottom: 8, marginTop: 0 }}>Boletines Oceanográficos</h4>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginTop: "12px" }}>
                              {(["peru", "ecuador", "oman", "panama"] as const).map((country) => {
                                const countryMeta: Record<string, { label: string; flag: string }> = {
                                  peru:    { label: "Perú",    flag: "🇵🇪" },
                                  ecuador: { label: "Ecuador", flag: "🇪🇨" },
                                  oman:    { label: "Omán",    flag: "🇴🇲" },
                                  panama:  { label: "Panamá",  flag: "🇵🇦" },
                                };
                                const slugs = country === "peru"
                                  ? ["ui:newsletter:peru_1", "ui:newsletter:peru_2", "ui:newsletter:peru_summary"]
                                  : [`ui:newsletter:${country}`, `ui:newsletter:${country}_2`, `ui:newsletter:${country}_summary`];
                                const countryPerms = slugs.filter((s) => availablePlanPermissions.includes(s));
                                if (countryPerms.length === 0) return null;
                                return (
                                  <div key={country} style={{ paddingLeft: 8, marginBottom: 8 }}>
                                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#666", marginBottom: 4 }}>
                                      {countryMeta[country].flag} {countryMeta[country].label}
                                    </div>
                                    <Space direction="vertical" size={2}>
                                      <Space direction="horizontal" wrap>
                                        {countryPerms.includes(slugs[0]) && (
                                          <Checkbox value={slugs[0]}>
                                            {getPermissionLabel(slugs[0]).split(" — ")[1] || "Boletín 1"}
                                          </Checkbox>
                                        )}
                                        {countryPerms.includes(slugs[1]) && (
                                          <Checkbox value={slugs[1]}>
                                            {getPermissionLabel(slugs[1]).split(" — ")[1] || "Boletín v3"}
                                          </Checkbox>
                                        )}
                                      </Space>
                                      {countryPerms.includes(slugs[2]) && (
                                        <Checkbox value={slugs[2]} style={{ marginTop: "4px" }}>
                                          <span style={{ color: "#d48806", fontWeight: 500 }}>★ Recibe Resumen (PPTX)</span>
                                        </Checkbox>
                                      )}
                                    </Space>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {availablePlanPermissions.length === 0 && (
                           <div style={{ fontStyle: "italic", color: "#8c8c8c" }}>
                              No hay permisos disponibles en el plan de la empresa matriz.
                           </div>
                        )}
                      </div>
                      </Checkbox.Group>
                    </Form.Item>
                  </div>
                ) : (
                  <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                    <PermissionBadgeList 
                      permissions={form.getFieldValue("permissions") || []} 
                      grouped={true} 
                    />
                  </div>
                )}
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
}

export default UserModal;
